import type { ReactNode } from 'react';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';
import { Skeleton } from '@shigo-ui/ui';

import { dashboardErrorMessage } from '../../../../lib/apiErrorMessages';
import { dashboardT } from '../../../../lib/messages';
import type { TargetingClient } from '../../api';
import {
  resolveTargetingFormSections,
  targetingSectionIsVisible,
  TargetingConsumer,
  TargetingFormSection,
  type TargetingFormConfig,
} from '../../config';
import type { TargetingFormValue } from '../../model';
import { useTargetingFormOptions } from '../../useTargetingFormOptions';
import { DeviceTypesTargetingSection } from './DeviceTypesTargetingSection';
import { ContentTargetingSection } from './ContentTargetingSection';
import {
  ExtraParametersTargetingSection,
  PlatformsTargetingSection,
  PmpDealIdsTargetingSection,
  SpecialHandlingTargetingSection,
} from './DemandIntegrationTargetingSections';
import { GeolocationTargeting } from './GeolocationTargeting/GeolocationTargeting';
import { LanguagesTargetingSection } from './LanguagesTargetingSection';
import { PerformanceTargetingSection } from './PerformanceTargetingSection';
import { PlacementsTargetingSection } from './PlacementsTargetingSection';
import { ContextualTargetingSection } from './ContextualTargetingSection';
import { LegacyLiverampTargetingSection } from './LegacyLiverampTargetingSection';
import type { TargetingCsvControl, TargetingFormUpdate } from './TargetingForm.types';
import { TargetingCsvSection } from './TargetingCsvSection';
import { TargetingSection } from './TargetingSection';
import { TargetingSupplyFields } from './TargetingSupplyFields/TargetingSupplyFields';
import { ShigoTargetingSupplyFields } from './ShigoTargetingSupplyFields';
import { ZipCodesTargetingSection } from './ZipCodesTargetingSection';

export function TargetingForm({
  config,
  csvControls,
  errors,
  leadingFields,
  liveRampAllowed = false,
  client,
  onChange,
  submitted,
  value,
}: {
  config: TargetingFormConfig;
  csvControls?:
    | {
        bundles?: TargetingCsvControl | undefined;
        ips?: TargetingCsvControl | undefined;
        zipCodes?: TargetingCsvControl | undefined;
        ipAddresses?: TargetingCsvControl | undefined;
      }
    | undefined;
  client: TargetingClient;
  errors?: Partial<Record<'ctr' | 'viewability', string>> | undefined;
  leadingFields?: ReactNode | undefined;
  liveRampAllowed?: boolean | undefined;
  onChange: (value: TargetingFormValue) => void;
  submitted: boolean;
  value: TargetingFormValue;
}) {
  const sections = resolveTargetingFormSections(config);
  const options = useTargetingFormOptions({
    client,
    sections,
  });
  const updateField: TargetingFormUpdate = (key, nextValue) => {
    onChange({ ...value, [key]: nextValue });
  };
  const visible = (section: TargetingFormSection) => targetingSectionIsVisible(sections, section);

  return (
    <DashboardPageFormBody
      width="lg"
      title={dashboardT('targeting.targeting')}
      description={dashboardT('targeting.targetingDescription')}
    >
      {leadingFields}
      {options.error ? (
        <p role="alert" className="text-destructive">
          {dashboardErrorMessage(options.error)}
        </p>
      ) : null}
      {options.loading ? (
        <div
          role="status"
          aria-label={dashboardT('targeting.loadingTargetingOptions')}
          className="grid gap-3"
        >
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : null}

      {!options.loading && visible(TargetingFormSection.Content) ? (
        <ContentTargetingSection
          iabCategories={options.iabCategories}
          updateField={updateField}
          value={value}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.Platforms) ? (
        <PlatformsTargetingSection
          platforms={options.platforms}
          updateField={updateField}
          value={value}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.Bundles) ? (
        <TargetingCsvSection
          title={dashboardT('integrations.targeting.bundles')}
          included={value.includeBundles}
          control={csvControls?.bundles}
          onIncludedChange={(included) => {
            updateField('includeBundles', included);
          }}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.IpTargeting) ? (
        <TargetingCsvSection
          title={dashboardT('integrations.targeting.ipTargeting')}
          included={value.includeIps}
          control={csvControls?.ips}
          onIncludedChange={(included) => {
            updateField('includeIps', included);
          }}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.DeviceTypes) ? (
        <DeviceTypesTargetingSection
          deviceTypes={options.deviceTypes}
          updateField={updateField}
          value={value}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.SpecialHandling) ? (
        <SpecialHandlingTargetingSection updateField={updateField} value={value} />
      ) : null}
      {!options.loading && visible(TargetingFormSection.Performance) ? (
        <PerformanceTargetingSection
          errors={errors}
          submitted={submitted}
          updateField={updateField}
          value={value}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.Languages) ? (
        <LanguagesTargetingSection
          consumer={config.consumer}
          languages={options.languages}
          updateField={updateField}
          value={value}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.Geolocations) ? (
        <TargetingSection title={dashboardT('targeting.geolocations')}>
          <GeolocationTargeting
            client={client}
            selectedLocations={value.geoLocations}
            onSelectedLocationsChange={(locations) => {
              updateField('geoLocations', locations);
            }}
          />
        </TargetingSection>
      ) : null}
      {!options.loading && visible(TargetingFormSection.ZipCodes) ? (
        <ZipCodesTargetingSection updateField={updateField} value={value} />
      ) : null}
      {!options.loading && visible(TargetingFormSection.ZipCodesImport) ? (
        <TargetingCsvSection
          title={dashboardT('targeting.zipCodes')}
          control={csvControls?.zipCodes}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.IpAddressesImport) ? (
        <TargetingCsvSection
          title={dashboardT('integrations.targeting.ipTargeting')}
          control={csvControls?.ipAddresses}
        />
      ) : null}
      {!options.loading &&
      config.consumer === TargetingConsumer.LineItem &&
      (visible(TargetingFormSection.Supply) || visible(TargetingFormSection.SupplyCompanies)) ? (
        <TargetingSupplyFields
          showSupply={visible(TargetingFormSection.Supply)}
          showSupplyCompanies={visible(TargetingFormSection.SupplyCompanies)}
          supplyCompanies={options.supplyCompanies}
          value={value}
          onChange={onChange}
        />
      ) : null}
      {!options.loading &&
      (config.consumer === TargetingConsumer.DemandConnection ||
        config.consumer === TargetingConsumer.Standalone) &&
      (visible(TargetingFormSection.Supply) ||
        visible(TargetingFormSection.Domains) ||
        visible(TargetingFormSection.TailoredSupply)) ? (
        <ShigoTargetingSupplyFields
          showApps={visible(TargetingFormSection.Supply)}
          showDomains={visible(TargetingFormSection.Domains)}
          showTailoredSupply={visible(TargetingFormSection.TailoredSupply)}
          value={value}
          onChange={onChange}
        />
      ) : null}
      {!options.loading && visible(TargetingFormSection.Contextual) ? (
        <ContextualTargetingSection updateField={updateField} value={value} />
      ) : null}
      {!options.loading && visible(TargetingFormSection.PmpDealIds) ? (
        <PmpDealIdsTargetingSection updateField={updateField} value={value} />
      ) : null}
      {!options.loading && liveRampAllowed && visible(TargetingFormSection.LiveRamp) ? (
        <LegacyLiverampTargetingSection client={client} value={value} onChange={onChange} />
      ) : null}
      {!options.loading && visible(TargetingFormSection.Placements) ? (
        <PlacementsTargetingSection client={client} value={value} onChange={onChange} />
      ) : null}
      {!options.loading && visible(TargetingFormSection.ExtraParameters) ? (
        <ExtraParametersTargetingSection updateField={updateField} value={value} />
      ) : null}
    </DashboardPageFormBody>
  );
}
